import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";
import { AddMachineModal } from "./AddMachineModal";
import { TabsViewer } from "../../../utils/TabsViewer";
import { MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import { CustomSelect } from "../../../components/CustomSelect";
import { LocationsGIS } from "../../../components/LocationsGIS";
import ReactImageFallback from "react-image-fallback";
import { DataTable } from "../../../components/DataTable";
import { Alert } from "../../../components/Alert";
import { TextInput } from "../../../components/core/inputs/TextInput";
import { TextAreaInput } from "../../../components/core/inputs/TextAreaInput";

export interface LocationEditorProps {
    locationId: number;
    canEdit: boolean;
    canDelete: boolean;
    canAssignMachines: boolean;
    defaultCountry: string;
    onClose: () => void;
    onAdd: () => void;
    onEdit: () => void;
    onDelete: () => void;
}
export const LocationEditor = ({locationId, canEdit, canDelete, canAssignMachines, defaultCountry, onClose, onAdd, onEdit, onDelete}: LocationEditorProps) => {
    const [location, setLocation] = useState<any|null>(null);
    const locationCountry = useMemo(() => location?.country, [location]);
    const [tempGeo, setTempGeo] = useState<string|null>(null);
    const [countries, setCountries] = useState<any[]>([]);
    const [timezones, setTimezones] = useState<any[]|null>(null);
    const [name, setName] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [changed, setChanged] = useState(false);
    const [deleteMachineId, setDeleteMachineId] = useState<number|null>(null);
    const [addMachine, setAddMachine] = useState(false);
    const { t } = useTranslation();

    const canEditLocation = useMemo(() => canEdit, [canEdit]);
    const canDeleteLocation = useMemo(() => canDelete && location?.lType == 0, [canDelete, location]);
    const canAssignMachinesP = useMemo(() => canAssignMachines && location?.lType == 0, [canAssignMachines, location]);

    const fetch = useCallback(() => {
        setError('');
        setChanged(false);
        API.GET('/api/config/countries')
            .then(setCountries)
            .catch(err => setError(err.message))
            .finally(() => {
                if (locationId > 0) {
                    setLoading(true);
                    API.GET('/api/admlocations/'+locationId)
                        .then(l => {
                            setLocation(l);
                            setName(l.Name);
                        })
                        .catch(err => setError(err.message))
                        .finally(() => setLoading(false));
                }else {
                    setLocation({
                        Name: '', address: '', Info: '', geo: '', machines: [], country: defaultCountry
                    });
                }
            });
    }, [locationId]);

    const locationChange = useCallback((key: string, val: any) => {
        setLocation(prev => ({
            ...prev,
            [key]: val
        }));
        setChanged(true);
        setError('');
    }, []);

    const update = useCallback(() => {
        setLoading(true);
        API.PATCH('/api/admlocations/'+locationId, {
            Name: location.Name,
            address: location.address,
            Info: location.Info,
            geo: location.geo,
            country: location.country,
            GMT: location.GMT
        })
            .then(() => { fetch(); onEdit(); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [locationId, location]);

    const insert = useCallback(() => {
        setLoading(true);
        API.POST('/api/admlocations', {
            Name: location.Name,
            address: location.address,
            Info: location.Info,
            geo: location.geo,
            country: location.country
        })
            .then(() => onAdd())
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [location]);

    useEffect(() => {
        setTimezones(null);
        if (!locationCountry) return;
        API.GET(`/api/config/countries/${locationCountry}/timezones`)
            .then(tzs => {
                setTimezones(tzs.map(tz => {
                    const hours = Math.floor(Math.abs(tz.offset)/60);
                    const negative = tz.offset > 0;
                    const minutes = Math.abs(tz.offset) - hours*60;

                    return {
                        name: tz.name,
                        offsetString: `${negative?'-':'+'}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                    }
                }));
            })
            .catch(err => setError(err.message))
    }, [locationCountry]);


    useEffect(() => {
        fetch();
    }, [locationId]);

    if (!location) return null;
    return (
        <div className="d-flex flex-column card h-100 overflow-hidden">
            <button type="button" className="btn-close position-absolute" style={{right: 5, top: 5}} onClick={() => onClose ? onClose() : {}}></button>
            <DeleteConfirmModal
                open={deleteMachineId !== null}
                title={t('remove_machine')}
                message={t('remove_machine_prompt')}
                onCancel={() => setDeleteMachineId(null)}
                onConfirm={() => {
                    API.DELETE(`/api/admlocations/${locationId}/m/${deleteMachineId}`)
                        .then(() => { setDeleteMachineId(null); fetch(); onEdit(); })
                    setDeleteMachineId(null);
                }}
            />
            <AddMachineModal
                open={addMachine}
                locationId={locationId}
                onClose={() => setAddMachine(false)}
                onConfirm={mid => {
                    setAddMachine(false);
                    API.POST(`/api/admlocations/${locationId}/m/${mid}`)
                        .then(() => { fetch(); onEdit(); });
                }}
            />
            <div className="d-flex flex-column card-body h-100 overflow-auto">
                <div className="d-flex flex-column w-100 justify-content-center">
                    <i className="fas fa-map-marker-alt fa-4x mb-2"/>
                    
                    
                    {loading && (
                        <div className="d-flex w-100 justify-content-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">{t('loading...')}</span>
                            </div>
                        </div>
                        
                    )}
                    {!loading && <>
                        <h5 className="text-center">{locationId == -1 ? t('new_location') : name}</h5>
                        { location?.lType == 1 && <div className="alert alert-warning alert-dismissible fade show mt-2" role="alert">
                            <i className="fas fa-bookmark text-warning me-1"/>{t('special_unassigned_location_prompt')}
                        </div> }
                        <div className="d-flex flex-column pe-1 pb-2">
                            {(locationId !== -1 && (location.machines !== undefined || location.groups !== undefined)) && <>
                                <div className="d-flex flex-column overflow-hidden align-items-center">
                                    <TabsViewer
                                        id="locationdata" fullSize
                                        defaultTab="info"
                                        tabs={[
                                            {
                                                name: 'info', title: t('location'), icon: 'fas fa-location-dot',
                                                component: (
                                                    <div className="d-flex flex-column w-100 overflow-auto px-1 pb-3 mb-2">
                                                       <h5 className="ms-2 mb-3">{t('information')}</h5>
                                                        { !canEditLocation && <>
                                                            <ul className="list-group list-group-light p-0">
                                                                <li className="list-group-item p-1"><i className="fas fa-map-location fa-fw me-1"/><span className="fw-bold me-1">{t('address')}:</span>{location.address}</li>
                                                                {location?.lType !== -1 && <li className="list-group-item p-1"><i className="fas fa-circle-info fa-fw me-1"/><span className="fw-bold me-1">{t('info')}:</span>{location.Info}</li>}
                                                                {location?.lType !== -1 && <li className="list-group-item p-1"><i className="fas fa-location-dot fa-fw me-1"/><span className="fw-bold me-1">{t('geo')}:</span>{location.geo}</li>}
                                                                {location?.lType !== -1 && <li className="list-group-item p-1"><i className="fas fa-location-dot fa-fw me-1"/><span className="fw-bold me-1">{t('country')}:</span>{locationCountry || '?'}</li>}
                                                            </ul>
                                                        </> }

                                                        { canEditLocation && <>
                                                            <TextInput label={t('name')} icon="pen-to-square" value={location.Name} onValueChange={val => locationChange('Name', val)}/>

                                                            {location.lType !== 1 && <>
                                                                <TextInput label={t('address')} icon="map-location" value={location.address} onValueChange={val => locationChange('address', val)}/>
                                                                <TextAreaInput label={t('info')} icon="circle-info" rows={2} value={location.Info} onValueChange={val => locationChange('Info', val)}/>
                                                                <TextInput label={t('geo')} icon="location-dot" value={location.geo} onValueChange={val => locationChange('geo', val)}/>

                                                                <div className="d-flex mb-2 flex-row align-items-center w-100">
                                                                    <i className="fas fa-earth-europe fa-fw me-2"/>
                                                                    <CustomSelect
                                                                        w100 title={t('country')} search={true}
                                                                        items={[
                                                                            {
                                                                                val: 'null',
                                                                                title: t('default')
                                                                            },
                                                                            ...countries.map(c => ({ val: c.code, title: `${c.country}`, icon: `flag-icon flag-icon-${c.code?.toLocaleLowerCase()}`}))
                                                                        ]}
                                                                        selected={location.country || 'null'}
                                                                        onSelect={code => {
                                                                            setLocation(l => ({...l, country: code == 'null' ? null : code}));
                                                                            setChanged(true);
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div className="d-flex mb-2 flex-row align-items-center">
                                                                    <MDBIcon icon="clock" fixed className="me-2"/>
                                                                    <div className="w-100">
                                                                        <CustomSelect expandWidth={245} title={t('timezone')}
                                                                            items={(timezones || []).map(tz => ({
                                                                                val: tz.name,
                                                                                title: `${tz.name} (${tz.offsetString})`
                                                                            }))}
                                                                            selected={location.GMT}
                                                                            onSelect={GMT => {setLocation(comp => ({...comp, GMT})); setChanged(true); setError(''); }}
                                                                            disabled={!timezones}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </>}
                                                            
                                                            
                                                        </> }

                                                        {location?.lType !== 1 &&
                                                            <div className="mb-3" style={{minHeight: 250, height: 250}}>
                                                                <LocationsGIS locations={[{id: -1, status: [], Name: name, geo: tempGeo??''}]} center={tempGeo??''} zoom={12} noPopups />
                                                            </div>
                                                        }

                                                        { error && <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
                                                            { error }
                                                        </div> }

                                                        <div className="d-flex flex-column">
                                                            { canEditLocation && <button className="btn btn-primary mb-2" disabled={!changed || loading} onClick={() => update()}>{t('save')}</button> }
                                                            { canDeleteLocation && <button className="btn btn-danger" disabled={loading} onClick={() => onDelete()}>{t('delete')}</button> }
                                                        </div>         

                                                    </div>
                                                )
                                            },
                                            ...(location.machines ? [{
                                                name: 'machines', title: t('machines'), icon: 'fas fa-server',
                                                component: ( <div className="d-flex flex-column w-100 mt-1">
                                                    { canAssignMachinesP && <div className="d-flex w-100 justify-content-end">
                                                        <MDBBtn color="link" onClick={() => setAddMachine(true)}>
                                                            <MDBIcon icon="plus-circle" className="me-1"/>
                                                            {t('add_machine')}
                                                        </MDBBtn>
                                                    </div> }
                                                    <DataTable<any>
                                                        height={250}
                                                        columns={[
                                                            {headerName: t('name'), valueGetter: ({data}) => ({name: data.name, type: data.type, sn: data.sn}), flex: 1, cellRenderer: ({value}) => (
                                                                <div className="d-flex flex-row align-items-center">
                                                                    <span><ReactImageFallback src={`/mtypes/mt${value.type?.id}.png`} width={22} height={22} className="me-1" title={value.type?.name}  fallbackImage={<span></span>}/></span>
                                                                    <div className="d-flex flex-column" style={{lineHeight: '1.1rem'}}>
                                                                        <span>{value.name}</span>
                                                                        <span className="text-muted" style={{fontSize: '80%'}}>{value.sn}</span>
                                                                    </div>
                                                                </div>
                                                            )},
                                                            {headerName: t('boxes'), field: 'nrOfBoxes', flex: 1},
                                                            ...(canAssignMachinesP ? [{headerName: t('actions'), valueGetter: ({data}) => data.id, flex: 1, cellRenderer: ({value}) => (
                                                                <span role="button" onClick={() => setDeleteMachineId(value)}>
                                                                    <i className="fas fa-trash-can text-danger"/>
                                                                </span>
                                                            )}] : [])
                                                        ]}
                                                        data={location.machines.sort((a,b) => b.nrOfBoxes - a.nrOfBoxes)}/>
                                                    </div>
                                                )
                                            }]:[]),
                                            ...(location.groups !== undefined ? [{
                                                name: 'groups', title: t('groups'), icon: 'fas fa-layer-group',
                                                component: ( <div className="d-flex flex-column w-100 mt-1">
                                                    <DataTable 
                                                        columns={[
                                                            {headerName: t('name'), field: 'Name', flex: 1}
                                                        ]}
                                                        data={location.groups}
                                                    />
                                                    </div>
                                                )
                                            }]:[])
                                        ]}
                                    />
                                </div>
                                
                            </>}

                            {locationId === -1 && <div className="d-flex flex-column w-100 overflow-auto px-1 mt-3 pt-2">
                                { (locationId == -1) && <>
                                    <TextInput label={t('name')} icon="pen-to-square" value={location.Name} onValueChange={val => locationChange('Name', val)}/>
                                    <TextInput label={t('address')} icon="map-location" value={location.address} onValueChange={val => locationChange('address', val)}/>
                                    <TextAreaInput label={t('info')} icon="circle-info" rows={2} value={location.Info} onValueChange={val => locationChange('Info', val)}/>
                                    <TextInput label={t('geo')} icon="location-dot" value={location.geo} onValueChange={val => locationChange('geo', val)}/>

                                    <div className="d-flex mb-2 flex-row align-items-center w-100">
                                        <i className="fas fa-earth-europe fa-fw me-2"/>
                                        <CustomSelect w100 title={t('country')} search={true} items={countries.map(c => ({ val: c.code, title: `${c.country}`, icon: `flag-icon flag-icon-${c.code?.toLocaleLowerCase()}`}))} selected={location.country} onSelect={code => {setLocation(l => ({...l, country: code})); setChanged(true); }} />
                                    </div>

                                    <div className="d-flex mb-2 flex-row align-items-center">
                                        <MDBIcon icon="clock" fixed className="me-2"/>
                                        <div className="w-100">
                                            <CustomSelect expandWidth={245} title={t('timezone')}
                                                items={(timezones || []).map(tz => ({
                                                    val: tz.name,
                                                    title: `${tz.name} (${tz.offsetString})`
                                                }))}
                                                selected={location.GMT}
                                                onSelect={GMT => {setLocation(comp => ({...comp, GMT})); setChanged(true); setError(''); }}
                                                disabled={!timezones}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3" style={{minHeight: 250, height: 250}}>
                                        <LocationsGIS locations={[{id: -1, status: [], Name: name, geo: tempGeo??''}]} center={tempGeo??''} zoom={12} noPopups />
                                    </div>
                                    <Alert type="danger" message={error}/>

                                    <div className="d-flex flex-column">
                                        <button className="btn btn-primary mt-2" disabled={!changed || loading} onClick={() => insert()}>{t('add')} <i className="far fa-square-plus fa-lg"/></button>
                                    </div>
                                </> }
                            </div>}
                        </div>
                    </>}
                </div>
            </div>
        </div>
    )
}